import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { RaxBFF } from '@raxapi/booker.planner.bff.api';
import { AuthenticationService } from '@raxapp/auth/authentication.service';
import { DebugModeService } from '@raxapp/debugging/debug-mode.service';
import { processException } from '@raxapp/error-handling/process-exception.function';
import { ShowErrorPageService } from '@raxapp/error-handling/show-error-pages/error-page/show-error-page.service';
import { Location } from '@raxapp/multi-tenants/models/Location.model';
import { Tenant } from '@raxapp/multi-tenants/models/Tenant.model';
import { MultiTenantStore } from '@raxapp/multi-tenants/MultiTenant.store';
import { ICachedState } from '@raxapp/routing/ICachedState';
import { ShellDataService } from '@raxapp/shell/shell-data.service';
import { UserAccountService } from '@raxapp/user-accounts/user-account.service';
import { UserAccount } from '@raxapp/user-accounts/UserAccount.model';
import { SharedCacheService } from '@raxshared/cache.service';
import { Observer } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@Component({
    selector: 'rax-shell-loading-page',
    templateUrl: './loading-page.component.html',
    providers: [ShellDataService],
})
export class ShellLoadingPage implements OnInit {
    private readonly router = inject(Router);
    private readonly cacheService = inject(SharedCacheService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly showErrorPageService = inject(ShowErrorPageService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly shellDataService = inject(ShellDataService);
    private readonly debugModeService = inject(DebugModeService);
    private readonly userAccountService = inject(UserAccountService);
    private readonly multiTenantStore = inject(MultiTenantStore);

    public ngOnInit(): void {
        this.authenticationService.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated === true),
                switchMap(() => this.shellDataService.initShellView()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(this.onShellViewLoaded);
    }

    private onShellViewLoaded: Partial<Observer<RaxBFF.ShellView>> = {
        next: (shellViewResult) => {
            this.initDebugMode(shellViewResult?.debugMode);
            this.initMultiTenant(shellViewResult?.multiTenant);
            this.initUserAccount(shellViewResult?.userAccount);

            this.navigateToCachedStateOrHome();
        },
        error: (error) => {
            this.showErrorPageService.exceptionView.set(processException(error));
            this.showErrorPageService.show();
        },
    };

    private initDebugMode(isDebugModeActive: boolean | undefined): void {
        this.debugModeService.setIsActive(isDebugModeActive ?? false);
    }

    private initUserAccount(userAccount: RaxBFF.ShellViewUserAccount | undefined): void {
        const mappedUserAccount: UserAccount = {
            displayName: userAccount?.displayName,
            email: userAccount?.email,
            isSysAdmin: userAccount?.sysAdmin,
        };

        this.userAccountService.setUserAccount(mappedUserAccount);
    }

    private initMultiTenant(multiTenant: RaxBFF.ShellViewMultiTenant | undefined): void {
        const tenants: Tenant[] = (multiTenant?.tenants ?? []).map((multiTenantTenant) => {
            return Tenant.create({
                tenantId: multiTenantTenant.tenantId,
                tenantIdCode: multiTenantTenant.tenantIdCode,
                tenantName: multiTenantTenant.tenantName,
                locations: (multiTenant?.locations ?? [])
                    .filter((location) => location.tenantId === multiTenantTenant.tenantId)
                    .map((location) => {
                        return Location.create({
                            locationId: location.locationId,
                            locationIdCode: location.locationIdCode,
                            locationName: location.locationName,
                        });
                    }),
            });
        });
        this.multiTenantStore.setTenants(tenants);

        const locations: Location[] = (multiTenant?.locations ?? []).map((multiTenantLocation) => {
            return Location.create({
                locationId: multiTenantLocation.locationId,
                locationIdCode: multiTenantLocation.locationIdCode,
                locationName: multiTenantLocation.locationName,
            });
        });

        this.multiTenantStore.setLocations(locations);
    }

    private navigateToCachedStateOrHome(): void {
        const cachedStateToLoad = this.cacheService.getMemory<ICachedState>('stateToLoad');
        if (
            cachedStateToLoad != undefined &&
            cachedStateToLoad.url != undefined &&
            cachedStateToLoad.url.length > 0 &&
            cachedStateToLoad.url !== 'loading'
        ) {
            this.cacheService.removeMemory('stateToLoad');
            this.router.navigateByUrl(cachedStateToLoad.url);
        } else {
            this.router.navigateByUrl('tenant-list');
        }
    }
}
